import toast from 'react-hot-toast';
import { Dispatch } from 'redux';
import { Services } from '../../types/services';

const GET_OFFERINGS = 'GET_OFFERINGS';
const FETCH_OFFERING_ERROR = 'FETCH_OFFERING_ERROR';

interface Offering {
  id?: string;
  name?: string;
  [key: string]: unknown;
}

interface State {
  availableOfferings: Offering[];
  error: string;
}

const initialState: State = {
  availableOfferings: [],
  error: '',
};

interface Action {
  type: string;
  payload?: unknown;
}

export const reducer = (state = initialState, action: Action): State => {
  switch (action.type) {
    case GET_OFFERINGS:
      return { ...state, availableOfferings: action.payload as Offering[] };
    case FETCH_OFFERING_ERROR:
      return { ...state, error: action.payload as string };
    default:
      return state;
  }
};

export const actions = (services: Services) => {
  const axiosCall = services.axios.Axios();

  const getOfferings = (offerings: Offering[]) => ({ type: GET_OFFERINGS, payload: offerings });

  const offeringError = (error: string) => ({ type: FETCH_OFFERING_ERROR, payload: error });

  const getAllOfferings = () => async () => axiosCall.get('offerings/partners');

  const fetchAllOfferings = () => {
    return async (dispatch: Dispatch) => {
      try {
        const response = await axiosCall.get('offerings/partners');
        const partnerOfferings = (response as unknown as { data: { data: Offering[] } }).data.data;
        dispatch(getOfferings(partnerOfferings));
      } catch (error: unknown) {
        dispatch(offeringError((error as Error).message));
      }
    };
  };

  const fetchBitwerxOfferings = () => {
    return async (dispatch: Dispatch) => {
      try {
        const response = await axiosCall.get('/offerings/bitwerx');
        const bitwerxOfferings = (response as unknown as { data: { items: Offering[] } }).data
          .items;
        dispatch(getOfferings(bitwerxOfferings));
        return bitwerxOfferings;
      } catch (error: unknown) {
        dispatch(offeringError((error as Error).message));
      }
    };
  };

  const fetchBitwerxOffering = (bitwerxOfferingId: string) => {
    return async (dispatch: Dispatch) => {
      try {
        if (!bitwerxOfferingId) {
          dispatch(offeringError('no offering id found'));
          toast.error('Error: Something went wrong fetching the Bitwerx Offering', {
            duration: 7500,
            style: { minWidth: '25vw' },
          });
          return;
        }
        const response = await axiosCall.get(`/offerings/bitwerx/${bitwerxOfferingId}`);
        return (response as unknown as { data: { data: Offering } }).data.data;
      } catch (error: unknown) {
        dispatch(offeringError((error as Error).message));
      }
    };
  };

  const createBitwerxOffering = (bitwerxOffering: Record<string, unknown>) => {
    return async (dispatch: Dispatch) => {
      try {
        const res = await axiosCall.post('offerings/bitwerx/', { ...bitwerxOffering });
        return (res as unknown as { data: unknown }).data;
      } catch (err: unknown) {
        const error = (err as { response?: { data?: unknown }; request?: { data?: unknown } })
          .response?.data
          ? JSON.stringify((err as { response: { data: unknown } }).response.data, null, 4)
          : JSON.stringify(err, null, 4);
        dispatch(offeringError(error));
        toast.error(`Error: ${error}`, { duration: 7500, style: { minWidth: '25vw' } });
      }
    };
  };

  const updateBitwerxOffering = (
    bitwerxOfferingId: string,
    bitwerxOffering: Record<string, unknown>,
  ) => {
    return async (dispatch: Dispatch) => {
      try {
        if (!bitwerxOfferingId) {
          dispatch(offeringError('no offering id found'));
          toast.error('Error: Something went wrong updating the Bitwerx Offering', {
            duration: 7500,
            style: { minWidth: '25vw' },
          });
          return;
        }
        const res = await axiosCall.patch(`offerings/bitwerx/${bitwerxOfferingId}`, {
          ...bitwerxOffering,
        });
        return (res as unknown as { data: unknown }).data;
      } catch (err: unknown) {
        const error = (err as { response?: { data?: unknown } }).response?.data
          ? JSON.stringify((err as { response: { data: unknown } }).response.data, null, 4)
          : JSON.stringify(err, null, 4);
        dispatch(offeringError(error));
        toast.error(`Error: ${error}`, { duration: 7500, style: { minWidth: '25vw' } });
      }
    };
  };

  const deleteBitwerxOffering = (bitwerxOfferingId: string) => {
    return async (dispatch: Dispatch) => {
      try {
        if (!bitwerxOfferingId) {
          dispatch(offeringError('no offering id found'));
          toast.error('Error: Something went wrong deleting Bitwerx Offering', {
            duration: 7500,
            style: { minWidth: '25vw' },
          });
          return;
        }
        const res = await axiosCall.delete(`offerings/bitwerx/${bitwerxOfferingId}`);
        return (res as unknown as { data: unknown }).data;
      } catch (err: unknown) {
        const error = (err as { response?: { data?: unknown } }).response?.data
          ? JSON.stringify((err as { response: { data: unknown } }).response.data, null, 4)
          : JSON.stringify(err, null, 4);
        dispatch(offeringError(error));
        toast.error(`Error: ${error}`, { duration: 7500, style: { minWidth: '25vw' } });
        throw error;
      }
    };
  };

  const deleteBitwerxSubOffering = (bitwerxSubOfferingId: string) => {
    return async (dispatch: Dispatch) => {
      try {
        if (!bitwerxSubOfferingId) {
          dispatch(offeringError('no sub offering id found'));
          toast.error('Error: Something went wrong deleting Sub Offering', {
            duration: 7500,
            style: { minWidth: '25vw' },
          });
          return;
        }
        const res = await axiosCall.delete(`offerings/bitwerx/suboffering/${bitwerxSubOfferingId}`);
        return (res as unknown as { data: unknown }).data;
      } catch (err: unknown) {
        const error = (err as { response?: { data?: unknown } }).response?.data
          ? JSON.stringify((err as { response: { data: unknown } }).response.data, null, 4)
          : JSON.stringify(err, null, 4);
        dispatch(offeringError(error));
        toast.error(`Error: ${error}`, { duration: 7500, style: { minWidth: '25vw' } });
        throw error;
      }
    };
  };

  const createOffering =
    (partnerId: string, payload: Record<string, unknown> = {}) =>
    async () =>
      axiosCall.post('offerings/partner/' + partnerId, { ...payload });

  const updateOffering =
    (partnerId: string, payload: Record<string, unknown> = {}) =>
    async () =>
      axiosCall.patch('offerings/partner/' + partnerId, { ...payload });

  return {
    createBitwerxOffering,
    createOffering,
    deleteBitwerxOffering,
    deleteBitwerxSubOffering,
    fetchAllOfferings,
    fetchBitwerxOffering,
    fetchBitwerxOfferings,
    getAllOfferings,
    updateBitwerxOffering,
    updateOffering,
  };
};
